import { useAppSelector, useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import { Text, SimpleGrid } from '@chakra-ui/react';

import Card from 'components/card/Card';

import FormInput from 'components/fields/home/FormInput';
import SelectInput from 'components/fields/home/SelectInput';
import RadioInput from 'components/fields/home/RadioInput';
import getRadioYesNo from 'helpers/getRadioYesNo';

import {
  changePolicyInformation,
  removeMissingMandatoryField,
} from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

const PolicyInformation = ({ uploadLOA }: { uploadLOA?: boolean | null }) => {
  const isPersonalLOAEnabled = useFeatureFlag('enable-loa-personal-lines');
  const dispatch = useAppDispatch();

  const data = useAppSelector(
    (state) => state.homePersonalForm.data?.policy_information,
  );

  const onPolicyInformationChange = (event: any) => {
    const { name, value, checked, type } = event.target;
    const payload = type === 'checkbox' ? checked : value;

    dispatch(changePolicyInformation({ name, value: payload }));
  };

  const onLOAChange = (value: string) => {
    dispatch(
      changePolicyInformation({ name: 'is_loa', value: value === 'true' }),
    );
  };

  const onStatusToggle = (status: string) => {
    const isNewPolicy = status === 'new';

    dispatch(
      changePolicyInformation({ name: 'new_policy', value: isNewPolicy }),
    );
    dispatch(
      changePolicyInformation({ name: 'is_remarket', value: !isNewPolicy }),
    );

    if (isNewPolicy) {
      dispatch(removeMissingMandatoryField('Customer Code'));
    }
  };

  const getStatusValue = () => {
    if (data.new_policy) return 'new';
    if (data.is_remarket) return 'remarket';

    return null;
  };

  if (!data) return null;

  return (
    <Card mb="20px" id="sectionPolicyInformation">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Policy Information
      </Text>

      <SimpleGrid columns={4} spacing="20px">
        <FormInput
          section={SECTIONS.POLICY_INFORMATION}
          label="Insurance Company"
          name="insurance_company"
          value={data.insurance_company || ''}
          onChange={onPolicyInformationChange}
          disabled
          required
        />
        <SelectInput
          section={SECTIONS.POLICY_INFORMATION}
          label="Type"
          name="type"
          value={data.type || ''}
          onChange={onPolicyInformationChange}
          options={[
            { value: 'New', label: 'New' },
            { value: 'Quote', label: 'Quote' },
            { value: 'Renewal', label: 'Renewal' },
          ]}
          required
        />
        <FormInput
          section={SECTIONS.POLICY_INFORMATION}
          label="Binder Number"
          name="binder_number"
          value={data.binder_number || ''}
          onChange={onPolicyInformationChange}
          required
        />
        <FormInput
          section={SECTIONS.POLICY_INFORMATION}
          label="Policy Number"
          name="policy_number"
          value={data.policy_number || ''}
          onChange={onPolicyInformationChange}
        />
        <RadioInput
          label="Is this a new policy?"
          section={SECTIONS.POLICY_INFORMATION}
          options={[
            { value: 'new', label: 'New Business' },
            { value: 'remarket', label: 'Remarket' },
          ]}
          value={getStatusValue()}
          onChange={onStatusToggle}
          required
          stacked
        />
        <RadioInput
          section={SECTIONS.POLICY_INFORMATION}
          label="Is this an LOA?"
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(data.is_loa)}
          onChange={onLOAChange}
          disabled={isPersonalLOAEnabled && uploadLOA != null}
          stacked
          required
        />
        <FormInput
          section={SECTIONS.POLICY_INFORMATION}
          label="Customer Code"
          name="customer_code"
          value={data.customer_code || ''}
          onChange={onPolicyInformationChange}
          required={data.is_remarket}
        />
        <FormInput
          section={SECTIONS.POLICY_INFORMATION}
          label="CSR Code"
          name="broker_csr_code"
          value={data.broker_csr_code || ''}
          onChange={onPolicyInformationChange}
          required
        />
      </SimpleGrid>
    </Card>
  );
};

export default PolicyInformation;
